import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { supabase } from '../lib/supabase';

// Helper to convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

// Helper to format date
function formatDate(dateString) {
  const date = new Date(dateString);
  return date?.toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
}

// Helper to format currency
function formatCurrency(amount) {
  return `$${Number(amount)?.toFixed(2)}`;
}

export const invoiceService = {
  // Fetch or create invoice for a booking
  async getOrCreateInvoice(bookingId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Check if invoice already exists
      const { data: existingInvoice, error: fetchError } = await supabase
        ?.from('invoices')
        ?.select('*')
        ?.eq('booking_id', bookingId)
        ?.single();

      if (fetchError && fetchError?.code !== 'PGRST116') {
        console.error('Error fetching invoice:', fetchError);
      }

      if (existingInvoice) {
        return toCamelCase(existingInvoice);
      }

      // If no invoice exists, fetch booking and create one
      const { data: booking, error: bookingError } = await supabase
        ?.from('bookings')
        ?.select('*')
        ?.eq('id', bookingId)
        ?.single();

      if (bookingError) {
        throw new Error('Failed to fetch booking details');
      }

      // Calculate platform fee and owner payout
      const platformFee = ((booking?.subtotal - booking?.discount) * 0.15)?.toFixed(2);
      const ownerPayout = ((booking?.subtotal - booking?.discount) * 0.85)?.toFixed(2);

      // Create new invoice
      const { data: newInvoice, error: createError } = await supabase
        ?.from('invoices')
        ?.insert({
          booking_id: bookingId,
          invoice_type: 'receipt',
          daily_rate: booking?.daily_rate,
          total_days: booking?.total_days,
          subtotal: booking?.subtotal,
          discount: booking?.discount || 0,
          tax: booking?.tax,
          tax_rate: 8.25,
          security_deposit: booking?.security_deposit,
          cleaning_fee: booking?.cleaning_fee,
          platform_fee: platformFee,
          platform_fee_percentage: 15.00,
          owner_payout: ownerPayout,
          total_amount: booking?.total_amount
        })
        ?.select()
        ?.single();

      if (createError) {
        throw new Error('Failed to create invoice');
      }

      return toCamelCase(newInvoice);
    } catch (error) {
      console.error('Get or create invoice error:', error);
      throw error;
    }
  },

  // Fetch booking details with related data
  async getBookingDetails(bookingId) {
    try {
      const { data: booking, error: bookingError } = await supabase
        ?.from('bookings')
        ?.select(`
          *,
          renter:user_profiles!renter_id(*)
        `)
        ?.eq('id', bookingId)
        ?.single();

      if (bookingError) {
        throw new Error('Failed to fetch booking details');
      }

      return toCamelCase(booking);
    } catch (error) {
      console.error('Get booking details error:', error);
      throw error;
    }
  },

  // Generate PDF invoice
  async generatePDF(bookingId, trailerDetails = {}) {
    try {
      // Fetch invoice and booking details
      const [invoice, booking] = await Promise.all([
        this.getOrCreateInvoice(bookingId),
        this.getBookingDetails(bookingId)
      ]);

      // Create PDF document
      const doc = new jsPDF();
      const pageWidth = doc?.internal?.pageSize?.getWidth();
      const pageHeight = doc?.internal?.pageSize?.getHeight();

      // Colors
      const primaryColor = [59, 130, 246]; // Blue
      const darkColor = [31, 41, 55];
      const mutedColor = [107, 114, 128];

      // Header - Company Info
      doc?.setFillColor(primaryColor?.[0], primaryColor?.[1], primaryColor?.[2]);
      doc?.rect(0, 0, pageWidth, 40, 'F');
      
      doc?.setTextColor(255, 255, 255);
      doc?.setFontSize(24);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Draggin Trailers LLC', 15, 20);
      
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'normal');
      doc?.text('Trailer Rental Marketplace', 15, 28);
      doc?.text('www.draggintrailers.com', 15, 34);

      // Invoice Type and Number
      doc?.setFontSize(18);
      doc?.setFont('helvetica', 'bold');
      doc?.text('RECEIPT', pageWidth - 15, 20, { align: 'right' });
      
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'normal');
      doc?.text(`Invoice #: ${invoice?.invoiceNumber || 'N/A'}`, pageWidth - 15, 28, { align: 'right' });
      doc?.text(`Date: ${formatDate(invoice?.issueDate || new Date())}`, pageWidth - 15, 34, { align: 'right' });

      // Reset text color
      doc?.setTextColor(darkColor?.[0], darkColor?.[1], darkColor?.[2]);

      // Billing Information
      let yPos = 55;
      doc?.setFontSize(12);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Bill To:', 15, yPos);
      
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'normal');
      yPos += 7;
      doc?.text(booking?.renter?.fullName || 'N/A', 15, yPos);
      yPos += 5;
      doc?.text(booking?.renter?.email || 'N/A', 15, yPos);

      // Booking Information
      yPos = 55;
      doc?.setFontSize(12);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Booking Details:', pageWidth - 15, yPos, { align: 'right' });
      
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'normal');
      yPos += 7;
      doc?.text(`Booking Ref: ${booking?.bookingReference || 'N/A'}`, pageWidth - 15, yPos, { align: 'right' });
      yPos += 5;
      doc?.text(`Rental Period: ${formatDate(booking?.startDate)} - ${formatDate(booking?.endDate)}`, pageWidth - 15, yPos, { align: 'right' });
      yPos += 5;
      doc?.text(`Status: ${booking?.status?.toUpperCase() || 'N/A'}`, pageWidth - 15, yPos, { align: 'right' });

      // Trailer Information
      yPos = 90;
      doc?.setFontSize(12);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Trailer Information:', 15, yPos);
      
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'normal');
      yPos += 7;
      doc?.text(trailerDetails?.title || 'Trailer Rental', 15, yPos);
      if (trailerDetails?.type) {
        yPos += 5;
        doc?.text(`Type: ${trailerDetails?.type}`, 15, yPos);
      }
      if (trailerDetails?.location) {
        yPos += 5;
        doc?.text(`Location: ${trailerDetails?.location}`, 15, yPos);
      }

      // Itemized Breakdown Table
      yPos = 120;
      doc?.autoTable({
        startY: yPos,
        head: [['Description', 'Quantity', 'Rate', 'Amount']],
        body: [
          [
            `Daily Rental Rate`,
            `${invoice?.totalDays || 0} days`,
            formatCurrency(invoice?.dailyRate || 0),
            formatCurrency(invoice?.subtotal || 0)
          ],
          ...(invoice?.discount > 0 ? [[
            'Weekly Discount',
            '-',
            '-',
            `-${formatCurrency(invoice?.discount)}`
          ]] : []),
          [
            'Cleaning Fee',
            '1',
            formatCurrency(invoice?.cleaningFee || 0),
            formatCurrency(invoice?.cleaningFee || 0)
          ],
          [
            'Security Deposit (Refundable)',
            '1',
            formatCurrency(invoice?.securityDeposit || 0),
            formatCurrency(invoice?.securityDeposit || 0)
          ],
          [
            `Tax (${invoice?.taxRate || 8.25}%)`,
            '-',
            '-',
            formatCurrency(invoice?.tax || 0)
          ]
        ],
        theme: 'striped',
        headStyles: {
          fillColor: primaryColor,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10
        },
        bodyStyles: {
          fontSize: 9,
          textColor: darkColor
        },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 30, halign: 'center' },
          2: { cellWidth: 35, halign: 'right' },
          3: { cellWidth: 35, halign: 'right' }
        },
        margin: { left: 15, right: 15 }
      });

      // Total Amount
      yPos = doc?.lastAutoTable?.finalY + 10;
      doc?.setDrawColor(mutedColor?.[0], mutedColor?.[1], mutedColor?.[2]);
      doc?.line(15, yPos, pageWidth - 15, yPos);
      
      yPos += 8;
      doc?.setFontSize(14);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Total Amount Paid:', pageWidth - 70, yPos);
      doc?.setTextColor(primaryColor?.[0], primaryColor?.[1], primaryColor?.[2]);
      doc?.text(formatCurrency(invoice?.totalAmount || 0), pageWidth - 15, yPos, { align: 'right' });
      doc?.setTextColor(darkColor?.[0], darkColor?.[1], darkColor?.[2]);

      // Platform Fee Breakdown (for transparency)
      yPos += 15;
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Platform Fee Breakdown:', 15, yPos);
      
      doc?.setFont('helvetica', 'normal');
      doc?.setFontSize(9);
      yPos += 6;
      doc?.text(`Platform Service Fee (${invoice?.platformFeePercentage || 15}%): ${formatCurrency(invoice?.platformFee || 0)}`, 15, yPos);
      yPos += 5;
      doc?.text(`Owner Payout (${100 - (invoice?.platformFeePercentage || 15)}%): ${formatCurrency(invoice?.ownerPayout || 0)}`, 15, yPos);

      // Payment Information
      yPos += 12;
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Payment Information:', 15, yPos);
      
      doc?.setFont('helvetica', 'normal');
      doc?.setFontSize(9);
      yPos += 6;
      doc?.text(`Payment Method: Credit Card ••••${booking?.paymentIntentId?.slice(-4) || 'XXXX'}`, 15, yPos);
      yPos += 5;
      doc?.text(`Transaction ID: ${booking?.paymentIntentId || 'N/A'}`, 15, yPos);
      yPos += 5;
      doc?.text(`Payment Status: ${booking?.paymentStatus?.toUpperCase() || 'N/A'}`, 15, yPos);

      // Footer
      yPos = pageHeight - 30;
      doc?.setDrawColor(mutedColor?.[0], mutedColor?.[1], mutedColor?.[2]);
      doc?.line(15, yPos, pageWidth - 15, yPos);
      
      yPos += 7;
      doc?.setFontSize(8);
      doc?.setTextColor(mutedColor?.[0], mutedColor?.[1], mutedColor?.[2]);
      doc?.text('Thank you for choosing Draggin Trailers LLC!', pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc?.text('Security deposit will be refunded within 3-5 business days after trailer return and inspection.', pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc?.text('For questions or support, contact us at support@draggintrailers.com', pageWidth / 2, yPos, { align: 'center' });

      // Generate filename
      const filename = `Receipt_${invoice?.invoiceNumber || booking?.bookingReference || 'Invoice'}.pdf`;

      // Save PDF
      doc?.save(filename);

      return { success: true, filename };
    } catch (error) {
      console.error('Generate PDF error:', error);
      throw error;
    }
  }
};